<template>
  <div id="thread-container" ref="threadContainer" @scroll="handleScroll">
    <div v-if="!Object.keys(thread).length">
      <p class="small-reg" v-if="isNewDate(initialMessage.timestamp, 0)">
        {{ dateToString(initialMessage.timestamp) }}
      </p>
      <ChatMessage :msg="initialMessage" :messageSignature="messageSignature"></ChatMessage>
    </div>

    <div v-for="(value, key, i) in thread" :key="key">
      <div v-if="(value.role && !value.role.startsWith('function') && value.role != 'system')">
        <p class="small-reg" v-if="isNewDate(value.timestamp, i)">
          {{ dateToString(value.timestamp) }}
        </p>
        <ChatMessage v-if="value.action == 'submit_contact_info' || value.action == 'update_contact_info'" :msg="value" :messageSignature="messageSignature" :canShowContactForm="true"></ChatMessage>
        <ChatMessage v-else :msg="value" :messageSignature="messageSignature" :canShowContactForm="(!lead.name && (!lead.email || !lead.phone)) || lastMessage.id == key"></ChatMessage>
      </div>
      <div v-else-if="value.action || (value.type.startsWith('function_call') && value.name)" class="context-msg">
        <div v-if="value.action == 'submit_contact_info'" class="chat-output">
          <div>You submitted the following contact information:</div>
          <div style="display: flex; flex-direction: column; margin: 0 auto; align-items: flex-start;">
            <div style="font-weight: 600;">👤 {{ value.contactInfo.name }}</div>
            <div v-if="value.contactInfo.email" style="font-weight: 600;">✉️ {{ value.contactInfo.email }}</div>
            <div v-if="value.contactInfo.phone" style="font-weight: 600;">📱 {{ value.contactInfo.phone }}</div>
          </div>
        </div>
        <div v-else-if="value.action == 'update_contact_info'" class="chat-output">
          <div>You updated your contact information:</div>
          <div style="display: flex; flex-direction: column; margin: 0 auto; align-items: flex-start;">
            <div style="font-weight: 600;">👤 {{ value.contactInfo.name }}</div>
            <div v-if="value.contactInfo.email" style="font-weight: 600;">✉️ {{ value.contactInfo.email }}</div>
            <div v-if="value.contactInfo.phone" style="font-weight: 600;">📱:{{ value.contactInfo.phone }}</div>
          </div>
        </div>
        <div v-else-if="value.type == 'function_call_output' && value.name == 'query_rv_database'" class="chat-output">
          <div v-if="typeof value.output === 'string' && value.output.endsWith('Narrow down your search.')">
            {{ extractNumbers(value.output) }} RVs found. ☝️⬆
          </div>
          <div v-else-if="isJsonArray(value.output)">
            {{ JSON.parse(value.output).length }} RVs found. ☝️⬆
          </div>
        </div>
        <div v-else-if="value.type == 'function_call_output' && value.name == 'submit_lead'" class="chat-output">
          <div>✅ Contact information received.</div>
        </div>
        <div v-else-if="value.type == 'function_call_output' && value.name == 'submit_tour_request'" class="chat-output">
          <div>✅ Tour request received.</div>
        </div>
        <div v-else-if="value.type == 'function_call_output' && value.name == 'submit_service_request'" class="chat-output">
          <div>✅ Service request received.</div>
        </div>
        <div v-else-if="value.type == 'function_call' && value.name == 'query_rv_database'" class="chat-output">
          <div>🔍 {{ value.query_description }}</div>
        </div>
      </div>
    </div>
    <TypingIndicator v-if="assistantTyping"></TypingIndicator>
    <ChatMessage 
      v-if="showContactForm" 
      :msg="{ content: 'We can certainly help you via text!', type: 'show_contact_form', quick_responses: [], role: 'assistant', timestamp: new Date().getTime() }" 
      :messageSignature="messageSignature" 
      :canShowContactForm="showContactForm"
      :edit="true">
    </ChatMessage>
  </div>
  <!-- <div
    class="scroll-bottom-icon-wrapper"
    v-show="showScrollIcon"
    @click="scrollToBottom"
  >
    <img class="scroll-bottom-icon" alt="" src="../assets/scroll-down.svg" />
  </div> -->
  <div id="anchor"></div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import ChatMessage from "./ChatMessage.vue";
import TypingIndicator from "./TypingIndicator";
import { useWindowStateStore } from '@/stores/useWindowStateStore';
import { useMessagesStore } from '@/stores/useMessagesStore';
import { storeToRefs } from "pinia";

const props = defineProps({
  thread: { type: Object },
  assistantTyping: { type: Boolean },
  messageSignature: { type: String },
  lead: { type: Object },
});

const { chatWindowOpen, showContactForm } = storeToRefs(useWindowStateStore());
const { lastMessage } = storeToRefs(useMessagesStore());

let lastDate = null;
const threadContainer = ref(null);
const scrollPosition = ref(0);
const initialMessage = {
  role: "assistant",
  content:
    "Hi there! 👋 I can help you find your next RV or answer any questions you have about our dealership. How can I help you today?",
  timestamp: new Date().getTime(),
};

/**
 * Convert date to readable string
 * @param {number} milli
 */
function dateToString(milli) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const date = new Date(milli);
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Determine if message has a different date compared to last message
 * @param {number} milli
 * @param {number} index
 */
function isNewDate(milli, index) {
  if (index == 0) lastDate = null;
  const s = dateToString(milli);
  if (s != lastDate) {
    lastDate = s;
    return true;
  }
  return false;
}

/**
 * Check if a string contains a valid JSON array
 * @param {string} str
 */
function isJsonArray(str) {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed);
  } catch (e) {
    return false;
  }
}

/**
 * Extract only numbers from a string
 * @param {string} str
 */
function extractNumbers(str) {
  return str.replace(/\D/g, '');
}

// Function to scroll to the top of the last element in the thread container
function scrollToBottom() {
  nextTick(() => {
    const container = threadContainer.value;
    if (!container) return;

    const lastElement = container.lastElementChild;
    if (lastElement) {
      lastElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  });
}

const stopWatchingThread = watch(
  () => props.thread,
  () => {
    scrollToBottom();
  },
  { deep: true, immediate: true }
);

const stopWatchingChatWindowVisibility = watch(chatWindowOpen, () => {
  scrollToBottom();
});

const stopWatchingContactFormVisibility = watch(showContactForm, (isVisible) => {
  if (isVisible) scrollToBottom();
});

const stopWatchingAssistantTyping = watch(() => props.assistantTyping, () => {
  scrollToBottom();
});

const handleScroll = () => {
  scrollPosition.value =
  threadContainer.value.scrollHeight - threadContainer.value.scrollTop;
};

// // Computed property to determine whether to show scroll-bottom icon
// const showScrollIcon = computed(() => {
//   return scrollPosition.value > 600; // here default scrollPosition.value = nearly 325px but it will vary device screen to screen thats why added 200px more to achieve 300px for now.
// });

onMounted(() => {
  scrollToBottom();
});

onUnmounted(() => {
  stopWatchingThread();
  stopWatchingChatWindowVisibility();
  stopWatchingContactFormVisibility();
  stopWatchingAssistantTyping();
});
</script>

<style scoped>
#thread-container {
  width: 100%;
  display: flex;
  padding-top: 20px;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  overflow-y: scroll;
  background-color: white;
}

#thread-container::-webkit-scrollbar {
  display: none; /* Hide the scrollbar */
}

.small-reg {
  color: #6b5f86;
  text-align: center;
  margin-top: 0;
  margin-bottom: 18px;
}

.scroll-bottom-icon-wrapper {
  position: relative;
  bottom: 55px;
  left: calc(50% - 24px);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  .scroll-bottom-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    position: absolute;
    left: 0px;
    top: 0px;
    transform: scale(1.417);
  }
}

#thread-container * {
  overflow-anchor: none;
}

#anchor {
  overflow-anchor: auto;
  height: 1px;
}

.chat-output {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  border: 1px solid var(--gray-dark);
  border-radius: 5px;
  background-color: var(--white-dark);
  text-align: center;
  padding: 10px 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 40px;
  max-width: 420px;
  box-sizing: border-box;
}

.context-msg {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
